'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { useTaskFlowStore } from '@/lib/task-flow/taskFlowStore'
import SynCreateContentFlow from './flows/SynCreateContentFlow'
import SynEditProductFlow from './flows/SynEditProductFlow'
import SynBuildFunnelFlow from './flows/SynBuildFunnelFlow'
import SynCreatePortraitFlow from './flows/SynCreatePortraitFlow'
import SynCreateFlyerFlow from './flows/SynCreateFlyerFlow'
import GenericTaskFlow from './flows/GenericTaskFlow'
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'

export default function GlobalTaskFlowHost() {
  const { isOpen, request, phase, closeTaskFlow, resetTaskFlow } = useTaskFlowStore()

  const [showCloseConfirm, setShowCloseConfirm] = useState<null | 'snapshot' | 'simple'>(null)

  const flowOverlayOpen = isOpen || showCloseConfirm

  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return

    document.documentElement.classList.toggle('hubee-flow-open', flowOverlayOpen)

    window.dispatchEvent(
      new CustomEvent('hubee:global-flow-visibility-change', {
        detail: { open: flowOverlayOpen },
      })
    )

    return () => {
      document.documentElement.classList.remove('hubee-flow-open')

      window.dispatchEvent(
        new CustomEvent('hubee:global-flow-visibility-change', {
          detail: { open: false },
        })
      )
    }
  }, [flowOverlayOpen])

  function handleAttemptClose() {
    if (!request?.taskType) {
      resetTaskFlow()
      return
    }

    // flows que usan snapshot (como create_content)
    if (request.taskType === 'create_content') {
      setShowCloseConfirm('snapshot')
      return
    }

    // flows sin snapshot (portrait, flyer, etc)
    setShowCloseConfirm('simple')
  }

  if (!request) return null

  function renderFlow() {
    switch (request.taskType) {
      case 'create_content':
        return <SynCreateContentFlow request={request} phase={phase} />
      case 'edit_product':
        return <SynEditProductFlow request={request} phase={phase} />
      case 'build_funnel':
        return <SynBuildFunnelFlow request={request} phase={phase} />
      case 'create_portrait':
        return <SynCreatePortraitFlow request={request} phase={phase} />
      case 'create_flyer':
        return <SynCreateFlyerFlow request={request} phase={phase} />
      default:
        return <GenericTaskFlow request={request} phase={phase} />
    }
  }

  if (!mounted) return null

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[300] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/55 backdrop-blur-md"
            onClick={handleAttemptClose}
          />

          <motion.div
            className="relative z-[301] flex h-[100dvh] min-h-0 w-full flex-col overflow-hidden md:h-[92vh] md:max-h-[92vh] md:w-[min(1100px,94vw)]"
            initial={{ opacity: 0, y: 18, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.985 }}
            transition={{ duration: 0.22 }}
          >
            <div className="min-h-0 flex-1 overflow-hidden">
              {renderFlow()}
            </div>

            <button
              type="button"
              onClick={handleAttemptClose}
              className="absolute right-4 top-4 rounded-full border border-cyan-400/20 bg-[#07111f]/90 px-3 py-2 text-xs text-cyan-200"
            >
              Cerrar
            </button>
          </motion.div>
        </motion.div>
      )}
      
      {showCloseConfirm && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md rounded-3xl border border-cyan-400/20 bg-[#07101d] p-6 text-cyan-50 shadow-2xl">

            <div className="text-xl font-semibold">
              Salir del flujo
            </div>

            <div className="mt-3 text-sm text-cyan-100/65 leading-relaxed">
              {showCloseConfirm === 'snapshot'
                ? 'Tienes un proyecto en progreso. ¿Quieres salir y guardarlo para continuar después, o descartarlo?'
                : 'Tienes cambios sin guardar. Si sales ahora, se perderán. ¿Deseas continuar?'}
            </div>

            <div className="mt-6 flex flex-col gap-2">

              {/* SEGUIR */}
              <button
                type="button"
                onClick={() => setShowCloseConfirm(null)}
                className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200"
              >
                Seguir aquí
              </button>

              {/* SNAPSHOT FLOW */}
              {showCloseConfirm === 'snapshot' && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCloseConfirm(null)
                      resetTaskFlow()
                    }}
                    className="rounded-2xl bg-cyan-400 px-4 py-2 text-sm font-semibold text-slate-950"
                  >
                    Guardar y salir
                  </button>

                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const snapshotId = useTaskFlowStore.getState().snapshotId

                        if (snapshotId) {
                          await fetch('/api/task-flows/create-content/abandon', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({ snapshotId }),
                          })
                        }
                      } catch (err) {
                        console.error('abandon snapshot error', err)
                      } finally {
                        setShowCloseConfirm(null)
                        resetTaskFlow()
                      }
                    }}
                    className="rounded-2xl border border-rose-400/30 bg-rose-400/10 px-4 py-2 text-sm text-rose-200"
                  >
                    Descartar proyecto
                  </button>
                </>
              )}

              {/* SIMPLE FLOW */}
              {showCloseConfirm === 'simple' && (
                <button
                  type="button"
                  onClick={() => {
                    setShowCloseConfirm(null)
                    resetTaskFlow()
                  }}
                  className="rounded-2xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white"
                >
                  Salir y perder cambios
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  )
}